import { groq } from "next-sanity"
import Helmet from "../components/Helmet"
import resolvePageProps from "../lib/resolvePageProps"
import usePage from "../lib/usePage"
import {
  englishPathForDisabledLocale,
  mergeEnabledLocales,
} from "../locales.config.mjs"

export default function Page({
  data,
  preview,
  globalSettings,
  enabledLocales,
  fileGroups,
  repDirectory,
}) {
  const { TemplateComponent, page } = usePage({
    data,
    isPage: true,
  })

  return (
    <>
      <Helmet page={page} enabledLocales={enabledLocales} />
      <TemplateComponent
        key={page._id}
        page={page}
        preview={preview}
        globalSettings={globalSettings}
        enabledLocales={enabledLocales}
        fileGroups={fileGroups}
        repDirectory={repDirectory}
      />
    </>
  )
}

export async function getServerSideProps({
  params,
  preview = false,
  query: queryString,
  locale,
  req,
  resolvedUrl,
}) {
  // A code in disabledLocaleCodes is not a Next locale, so that URL arrives as a slug.
  const englishPath = englishPathForDisabledLocale(resolvedUrl)
  if (englishPath) {
    return { redirect: { destination: englishPath, permanent: false } }
  }

  try {
    const { globalSettings, fileGroups, page, query, queryParams, redirect } =
      await resolvePageProps({
        params,
        query: queryString,
        preview,
        locale,
      })

    if (redirect)
      return {
        redirect: {
          destination: redirect,
          permanent: true,
        },
      }

    // Website locales are merged in so the switcher and hreflang list them
    // before the Sanity locales document is updated. Disabled codes stay out.
    const enabledLocales = mergeEnabledLocales(
      preview ? [] : globalSettings.locales?.enabledLocales
    )
    if (!page || !enabledLocales.includes(locale)) return { notFound: true }

    let repDirectory = null
    try {
      const { getRepDirectory } = await import("../lib/reps/getRepDirectory")
      const { headquartersContactFromSettings } = await import("../lib/reps/directory")
      repDirectory = await getRepDirectory({
        siteContact: headquartersContactFromSettings(globalSettings),
      })
    } catch (error) {
      console.error(error)
    }

    let visitorCountryCode = ""
    try {
      const { countryCodeFromLocale, preselectCountryCode, visitorCountryFromHeaders } =
        await import("../lib/geo/visitorCountry")
      visitorCountryCode =
        preselectCountryCode(visitorCountryFromHeaders(req?.headers)) ||
        countryCodeFromLocale(req?.headers?.["accept-language"])
    } catch (error) {
      console.error(error)
    }

    return {
      props: {
        // Pass down the "preview mode" boolean to the client-side
        preview,
        // Pass down the initial content, and our query
        globalSettings,
        enabledLocales,
        fileGroups,
        repDirectory,
        visitorCountryCode,
        data: {
          page,
          query,
          queryParams,
          draftQuery: groq`*[$slug == '/' && slug.current == *[_id == 'siteSettings'][0].homepage->.slug.current || slug.current == $slug]`,
          draftParams: queryParams,
        },
      },
    }
  } catch (e) {
    console.log(e)
    return { notFound: true }
  }
}
